import React, { useState, useEffect, FC, MouseEventHandler } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay';
import { isMasqueradingAsBuyer } from 'dibs-cookie-jar';
import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import Dropdown, { alignConstants, sizeConstants } from '../Dropdown/Dropdown';
import DropdownBody from '../Dropdown/DropdownBody';
import authModalLoader from '../../utils/AuthModalLoader';
import AccountNavTrigger from './AccountNavTrigger';
import BuyerAccountNavContent from './AccountNavContent';

import { ACTION_REGISTER, ACTION_LOGIN } from './accountConstants';
import styles from './AccountNav-style.scss';
import { actionCreators } from '../../actions';

import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';

import { AccountNav_user as UserType } from './__generated__/AccountNav_user.graphql';
import { AccountNav_viewer as ViewerType } from './__generated__/AccountNav_viewer.graphql';

const TradeAccountNavContentLazy = React.lazy(() =>
    import(
        /* webpackChunkName: "tradeAccountNavContent" */
        './TradeAccountNavContent'
    )
);

type ReduxStoreType = {
    header: {
        isVipCuratedFolderTooltipVisible: boolean;
    };
};

type AccountNavProps = {
    hasUser: boolean;
    hasUserId: boolean;
    isClient: boolean;
    user: UserType;
    viewer: ViewerType;
    relay: RelayProp;
};

const AccountNav: FC<AccountNavProps> = ({ hasUser, hasUserId, isClient, user, viewer, relay }) => {
    const [isMasquerading, setIsMasquerading] = useState(false);
    useEffect(() => {
        setIsMasquerading(isMasqueradingAsBuyer(document.cookie));
    }, []);

    const isVipCuratedFolderTooltipVisible = useSelector<ReduxStoreType, boolean>(
        ({ header }) => header.isVipCuratedFolderTooltipVisible
    );
    const dispatch = useDispatch();

    const handleClick: MouseEventHandler<HTMLButtonElement> = async e => {
        e.preventDefault();
        const action = e.currentTarget.dataset.action;
        try {
            const { authError } = await authModalLoader.show({
                relayEnvironment: relay.environment,
                email: localStorage.getItem('userEmail'),
                action,
                ga: {
                    label: `${action} link - global header`,
                    value: !!window.scrollY,
                },
            });
            if (!authError) {
                dispatch(actionCreators.setUpdateUserState(true));
            }
        } catch (error) {
            console.warn('Auth Modal could not be loaded\n', error);
        }
    };

    if (!isClient || (hasUserId && !hasUser)) {
        return null;
    }

    const name = user?.profile?.firstName || (
        <FormattedMessage id="dbl.Header.AccountNav.name" defaultMessage={'My Account'} />
    );

    const totalUnreadConversations = user?.conversationSummary?.unread || 0;
    const isVerifiedTrade = user?.isVerifiedTrade;

    return (
        <div data-tn="account-nav" className={styles.wrapper}>
            {hasUser ? (
                <>
                    <Dropdown
                        align={alignConstants.ALIGN_CENTER}
                        size={sizeConstants.SIZE_SMALL}
                        className={classnames({
                            [styles.noHover]: isVipCuratedFolderTooltipVisible,
                        })}
                    >
                        <AccountNavTrigger
                            isMasquerading={isMasquerading}
                            isVerifiedTrade={isVerifiedTrade}
                            name={name}
                        />
                        <DropdownBody>
                            {isVerifiedTrade ? (
                                <React.Suspense fallback={<DropdownLoadingContent />}>
                                    <TradeAccountNavContentLazy
                                        totalUnreadConversations={totalUnreadConversations}
                                        user={user}
                                    />
                                </React.Suspense>
                            ) : (
                                <BuyerAccountNavContent
                                    totalUnreadConversations={totalUnreadConversations}
                                    viewer={viewer}
                                    user={user}
                                />
                            )}
                        </DropdownBody>
                    </Dropdown>
                </>
            ) : (
                <div className={styles.authButtons}>
                    <button
                        className={styles.authButton}
                        data-tn="user-menu-nav-item-link-log-in"
                        data-action={ACTION_LOGIN}
                        onClick={handleClick}
                    >
                        <FormattedMessage
                            id="dbl.Header.AccountNav.log_in"
                            defaultMessage={'Log In'}
                        />
                    </button>
                    <button
                        className={styles.authButton}
                        data-tn="user-menu-nav-item-link-sign-up"
                        data-action={ACTION_REGISTER}
                        onClick={handleClick}
                    >
                        <FormattedMessage
                            id="dbl.Header.AccountNav.sign_up"
                            defaultMessage={'Sign Up'}
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

export default createFragmentContainer(AccountNav, {
    viewer: graphql`
        fragment AccountNav_viewer on Viewer {
            ...AccountNavContent_viewer
        }
    `,
    user: graphql`
        fragment AccountNav_user on User {
            ...AccountNavContent_user
            ...TradeAccountNavContent_user
            isVerifiedTrade
            profile {
                firstName
            }
            conversationSummary {
                unread
            }
        }
    `,
});
